import React, { useState, useEffect, useRef } from "react";
import { CloseIcon, ChevronDownIcon } from "../SvgIcon";
import _ from "lodash";
import {
  getRetrieveDiscounts,
  bulkDeleteDiscounts,
  getDiscountCounts,
} from "../../modules/Discount";
import {
  bulkDeleteCampaigns,
  getCampaignList,
  linkCouponToCampaign,
} from "../../modules/Campaign";
import {
  showSpinner,
  closeSpinner,
  modal,
  alert,
  confirm,
} from "../../modules/Dialogs";
import { sessionExpired } from "../../config/GeneralConfig";
import { toScreen } from "../../modules/Routing";
import PropTypes from "prop-types";
import withNavigate from "../withNavigate";
import moment from "moment-timezone";
import { Tooltip } from "@mui/material";

import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import Button from "@mui/material/Button";
import CreateDiscount from "./CreateDiscount";
import CreateCampaign from "./CreateCampaign";
import CreateCampaignModal from "./CreateCampaignModal";
import Pagination from "../Pagination";
import { getSafariCompatibleBoxSx } from "../../modules/DrawerUtils";

const Discounts = ({ userDetails, navigate }) => {
  const [state, setState] = React.useState({
    right: false,
  });
  const [currentView, setCurrentView] = useState(null);
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const drawerRef = useRef(null);
  const [activeTab, setActiveTab] = useState("discounts");
  const [searchValue, setSearchValue] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState(
    window.history.state?.usr?.selectedFilter ?? "all",
  );
  const [pageSize, setPageSize] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [inputValue, setInputValue] = useState(1);
  const [selectedCheckbox, setSelectedCheckbox] = useState([]);

  const [sort, setSort] = useState("");
  const [discounts, setDiscounts] = useState([]);

  // Campaign state
  const [campaigns, setCampaigns] = useState([]);
  const [campaignSearchValue, setCampaignSearchValue] = useState(null);
  const [campaignSearch, setCampaignSearch] = useState("");
  const [campaignFilter, setCampaignFilter] = useState("all");
  const [campaignPage, setCampaignPage] = useState(1);
  const [campaignPageSize, setCampaignPageSize] = useState(15);
  const [campaignTotalPages, setCampaignTotalPages] = useState(1);
  const [campaignTotalDocs, setCampaignTotalDocs] = useState(0);
  const [campaignInputValue, setCampaignInputValue] = useState(1);
  const [campaignSelectedCheckbox, setCampaignSelectedCheckbox] = useState([]);
  const [campaignSort, setCampaignSort] = useState("");
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCampaignForLink, setSelectedCampaignForLink] = useState(null);
  const [discountToLink, setDiscountToLink] = useState(null);
  const [campaignDropdownOpen, setCampaignDropdownOpen] = useState(false);
  const [campaignDropdownPosition, setCampaignDropdownPosition] =
    useState("bottom");
  const campaignDropdownRef = useRef(null);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [showCreateCampaignModal, setShowCreateCampaignModal] = useState(false);
  const [campaignsHasMore, setCampaignsHasMore] = useState(true);
  const [linkModalCampaignPage, setLinkModalCampaignPage] = useState(1);
  const linkModalCampaignPageSize = 10;

  const toggleDrawer =
    (anchor, open, view = null, discount = null) =>
    (event) => {
      if (
        event &&
        event.type === "keydown" &&
        (event.key === "Tab" || event.key === "Shift")
      ) {
        return;
      }

      setState({ ...state, [anchor]: open });

      if (open) {
        setCurrentView(view);
        if (discount) {
          setSelectedDiscount(discount);
        } else {
          setSelectedDiscount(null);
        }
      }
    };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isDrawerOpen &&
        drawerRef.current &&
        !drawerRef.current.contains(event.target)
      ) {
        closeDiscountDetails();
      }
    };

    // Add event listener when drawer is open
    if (isDrawerOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Cleanup listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDrawerOpen]);

  const closeDiscountDetails = () => {
    setIsDrawerOpen(false);
    setSelectedDiscount(null);
  };

  const [filterCount, setFilterCount] = useState({
    all: 0,
    active: 0,
    scheduled: 0,
    expired: 0,
    used: 0,
    revoked: 0,
    inactive: 0,
  });

  useEffect(() => {
    setInputValue(page);
  }, [page]);

  const searchJob = () => {
    setSearch(searchValue);
    setFilter(searchValue === "" ? "all" : null);
    setPage(1);
  };

  const sortDiscounts = (event, field) => {
    if (event) event.preventDefault();

    const sortFields = {
      type: { asc: "type=1", desc: "type=-1" },
      codes: { asc: "codes=1", desc: "codes=-1" },
      status: { asc: "status=1", desc: "status=-1" },
      startDate: { asc: "startDate=1", desc: "startDate=-1" },
      uses: { asc: "uses=1", desc: "uses=-1" },

      endDate: { asc: "endDate=1", desc: "endDate=-1" },
    };

    const currentSort = sortFields[field];
    if (!currentSort) return;

    const newSort =
      sort === currentSort.asc ? currentSort.desc : currentSort.asc;

    setSearch("");
    setSort(newSort);
    setPage(1);
  };
  const handleDiscountCreated = () => {
    setSelectedCheckbox([]);
    fetchFilterCount();
    if (page === 1) {
      getDiscounts();
    } else {
      setPage(1);
    }
  };
  const getDiscounts = React.useCallback(() => {
    showSpinner();

    const queryParams = {
      page: page,
      limit: pageSize,
    };

    if (search) queryParams.search = search;
    if (filter && filter !== "all") queryParams.status = filter;

    if (sort) {
      const parts = sort.split("=");
      const field = parts[0];
      const order = parts[1];
      if (field === "type") queryParams.typeSorter = order;
      else if (field === "codes") queryParams.codesSorter = order;
      else if (field === "status") queryParams.statusSorter = order;
      else if (field === "uses") queryParams.useSorter = order;
      else if (field === "startDate") queryParams.startDateSorter = order;
      else if (field === "endDate") queryParams.endDateSorter = order;
    }

    getRetrieveDiscounts(queryParams, getDiscounts_done, userDetails.jwttoken);
  }, [page, pageSize, search, filter, sort, userDetails.jwttoken]);

  const getDiscounts_done = (resStatus, responseString, resJson) => {
    closeSpinner();

    if (resStatus === 401) {
      modal(sessionExpired);
      toScreen(false, navigate, "/logout");
    } else if (resStatus === "400") {
      setDiscounts([]);
      setTotalPages(1);
      alert(responseString);
    } else if (resStatus === 200 || resStatus === "200") {
      const totalRecords = resJson?.totalRecords || 0;
      setTotalPages(Math.ceil(totalRecords / pageSize) || 1);

      const fetchedDiscounts =
        resJson?.payload?.discounts ||
        resJson?.payload ||
        resJson?.discounts ||
        resJson?.data ||
        [];
      setDiscounts(Array.isArray(fetchedDiscounts) ? fetchedDiscounts : []);
    }
  };

  const fetchFilterCount = React.useCallback(() => {
    getDiscountCounts((resStatus, responseString, resJson) => {
      if (resStatus === 200 || resStatus === "200") {
        const data = resJson?.data || {};
        const all =
          (data?.active || 0) +
          (data?.expired || 0) +
          (data?.scheduled || 0) +
          (data?.used || 0) +
          (data?.revoked || 0) +
          (data?.inactive || 0);
        setFilterCount({
          all: all,
          active: data?.active || 0,
          scheduled: data?.scheduled || 0,
          expired: data?.expired || 0,
          used: data?.used || 0,
          revoked: data?.revoked || 0,
          inactive: data?.inactive || 0,
        });
      }
    }, userDetails.jwttoken);
  }, [userDetails.jwttoken]);

  const handleDiscountCheckbox = (discountId) => {
    if (selectedCheckbox.includes(discountId)) {
      setSelectedCheckbox(
        selectedCheckbox.filter((item) => item !== discountId),
      );
    } else {
      setSelectedCheckbox([...selectedCheckbox, discountId]);
    }
  };

  const handleEditClick = (campaign) => {
    setEditingCampaign(campaign);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingCampaign(null);
  };

  const renderCampaigns = (campaign, index) => {
    return (
      <div className={`c-list-entry align-items-center`} key={index}>
        <div className="c-list-entry__name">
          <div className="campaign-list-title">{campaign?.name || "N/A"}</div>
        </div>

        <div className="c-list-entry__reason">
          <Tooltip
            title={campaign?.description || "-"}
            arrow
            placement="top"
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: "offset",
                    options: {
                      offset: [0, 1],
                    },
                  },
                ],
              },
              tooltip: {
                sx: {
                  backgroundColor: "#2d3748",
                  color: "#fff",
                  fontSize: "13px",
                  fontWeight: "500",
                  padding: "12px 16px",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                  maxWidth: "300px",
                  wordWrap: "break-word",
                  lineHeight: "1.5",
                },
              },
              arrow: {
                sx: {
                  visibility: "visible",
                  color: "#2d3748",
                  "&::before": {
                    border: "1px solid #2d3748",
                  },
                },
              },
              arrow: {
                sx: {
                  color: "#2d3748",
                  "&::before": {
                    border: "1px solid #2d3748",
                  },
                },
              },
            }}
          >
            <div className="campaign-list-description">
              {campaign?.description || "-"}
            </div>
          </Tooltip>
        </div>

        <div className="c-list-entry__jobdate">
          <div className="campaign-list-cell">
            {campaign?.discountCodesCount || 0}
          </div>
        </div>

        <div className="c-list-entry__jobdate">
          <div className="campaign-list-cell">
            {campaign?.activeCodesCount || 0}
          </div>
        </div>

        <div className="c-list-entry__uses">
          <span className="u-before-icon text-capitalize">
            &nbsp;&nbsp;
            {campaign?.totalUses || 0}
          </span>
        </div>

        <div className="c-list-entry__uses">
          {(() => {
            const campaignStatus =
              campaign?.status?.toLowerCase();
            const typeMap = {
              active: { label: "active", badge: "badge-success" },
              inactive: { label: "revoked", badge: "badge-danger" },
            };
            const typeData = typeMap[campaignStatus] || {
              label: campaign?.status || "-",
              badge: "badge-secondary",
            };
            return (
              <span className={`badge text-capitalize ${typeData?.badge}`}>
                {typeData?.label}
              </span>
            );
          })()}
        </div>

        <div className="c-list-entry__startDate">
          <span className="u-before-icon-calendar2">
            &nbsp;&nbsp;
            {campaign?.createdAt
              ? moment(campaign?.createdAt)
                  ?.tz("Europe/London")
                  .format("DD MMM YY")
              : "-"}
          </span>
        </div>

        <div className="c-list-entry__actions">
          <Button
            className="btn btn-sm btn-primary"
            onClick={() => handleEditClick(campaign)}
          >
            Edit
          </Button>
        </div>
      </div>
    );
  };

  const renderDiscounts = (discount, index) => {
    const applicableJobs = discount?.applicableJobType
      ? Array.isArray(discount.applicableJobType)
        ? discount.applicableJobType.map((job) => _.startCase(job)).join(", ")
        : _.startCase(discount.applicableJobType)
      : discount?.appliesTo === "allJobs"
        ? "All jobs"
        : "All jobs";

    return (
      <div className={`c-list-entry align-items-center`} key={index}>
        <div>
          <form>
            <div
              className={`c-list-entry select-all-checkbox ${discount?.computedStatus?.toLowerCase() === "revoked" && "u-visibility-hidden"}`}
            >
              <>
                <input
                  type="checkbox"
                  id={`checkbox-${discount?._id || index}`}
                  name="checkbox"
                  checked={selectedCheckbox.includes(discount?._id || index)}
                  onChange={(e) =>
                    handleDiscountCheckbox(discount?._id || index)
                  }
                />
                <label htmlFor={`checkbox-${discount?._id || index}`}></label>
              </>
            </div>
          </form>
        </div>
        <div className="c-list-entry__discountCode">
          <div className="discount-list-title">{discount?.code || "N/A"}</div>
          <div className="discount-list-meta">
            {discount?.value || "0"}% discount
            <span className="discount-list-meta-separator">•</span>
            Min. £{discount?.minOrderValue || 0}
            <span className="discount-list-meta-separator">•</span>
            {applicableJobs}
          </div>
          {(discount?.selectedUser ||
            discount?.perCustomerUsageLimitChecked) && (
            <div className="discount-list-assigned">
              {discount?.selectedUser
                ? `Assigned to ${discount.selectedUser}`
                : ""}
              {discount?.selectedUser &&
              discount?.perCustomerUsageLimitChecked ? (
                <span className="discount-list-meta-separator--sm">•</span>
              ) : (
                ""
              )}
              {discount?.perCustomerUsageLimitChecked
                ? "Once per customer"
                : ""}
            </div>
          )}
        </div>

        <div className="c-list-entry__jobdate">
          {(() => {
            const customerEligibility =
              discount?.customerEligibility?.toLowerCase();
            const typeMap = {
              new: { label: "New customers", badge: "badge-success" },
              everyone: { label: "Everyone", badge: "badge-warning" },
            };
            const typeData = typeMap[customerEligibility] || {
              label: discount?.customerEligibility || "-",
              badge: "badge-secondary",
            };
            return (
              <span className={`badge text-capitalize ${typeData.badge}`}>
                {typeData.label}
              </span>
            );
          })()}
        </div>
        <div className="c-list-entry__jobdate">
          {(() => {
            const type = discount?.type?.toLowerCase();
            const typeMap = {
              userspecific: { label: "User-Specific", badge: "badge-warning" },
              marketing: { label: "Marketing", badge: "badge-success" },
              referral: { label: "Referral", badge: "badge-info" },
              referralReward: { label: "Referral Reward", badge: "badge-info" },
            };
            const typeData = typeMap[type] || {
              label: discount?.type || "-",
              badge: "badge-secondary",
            };
            return (
              <span className={`badge text-capitalize ${typeData.badge}`}>
                {typeData.label}
              </span>
            );
          })()}
        </div>
        <div className="c-list-entry__discountStatus">
          {(() => {
            const status = discount?.computedStatus?.toLowerCase();
            const statusMap = {
              inactive: { label: "Inactive", badge: "badge-secondary" },
              scheduled: { label: "Scheduled", badge: "badge-secondary" },
              used: { label: "Used", badge: "badge-warning" },
              active: { label: "Active", badge: "badge-success" },
              expired: { label: "Expired", badge: "badge-secondary" },
              revoked: { label: "Revoked", badge: "badge-danger" },
            };
            const statusData = statusMap[status] || {
              label: discount?.computedStatus || "-",
              badge: "badge-secondary",
            };
            return (
              <span className={`badge text-capitalize ${statusData?.badge}`}>
                {statusData?.label}
              </span>
            );
          })()}
        </div>
        <div className="c-list-entry__uses">
          <span className="u-before-icon text-capitalize">
            &nbsp;&nbsp;
            {`${discount?.redemptionsCount || 0} / ${discount?.totalUsageLimit || 0}`}
          </span>
        </div>
        <div className="c-list-entry__startDate">
          <span className="u-before-icon-calendar2">
            &nbsp;&nbsp;
            {discount?.startDateTime
              ? moment(discount?.startDateTime)
                  ?.tz("Europe/London")
                  .format("DD MMM YYYY ")
              : "-"}
          </span>
          <br />
          <span className="u-before-icon-clock">
            &nbsp;&nbsp;
            {discount?.startDateTime
              ? moment(discount?.startDateTime)
                  ?.tz("Europe/London")
                  .format("hh:mm ")
              : "-"}
          </span>
        </div>
        <div className="c-list-entry__startDate">
          <span className="u-before-icon-calendar2">
            &nbsp;&nbsp;
            {discount?.endDateTime
              ? moment(discount?.endDateTime)
                  ?.tz("Europe/London")
                  .format("DD MMM YYYY ")
              : "-"}
          </span>
          <br />
          <span className="u-before-icon-clock">
            &nbsp;&nbsp;
            {discount?.endDateTime
              ? moment(discount?.endDateTime)
                  ?.tz("Europe/London")
                  .format("hh:mm ")
              : "-"}
          </span>{" "}
        </div>
        <div className="c-list-entry__discountAction">
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => {
              setDiscountToLink(discount);
              setSelectedCampaignForLink(null);
              setState({ ...state, right: true });
              setCurrentView("link");
            }}
          >
            Assign Campaign
          </button>
        </div>
      </div>
    );
  };

  const handleCampaignCreated = () => {
    setCampaignSelectedCheckbox([]);
    // fetchCampaignFilterCount();
    if (campaignPage === 1) {
      getCampaigns();
    } else {
      setCampaignPage(1);
    }
  };

  const fetchCampaignsForLinkModal = (page = 1, append = false) => {
    setCampaignsLoading(true);
    const queryParams = {
      page,
      limit: linkModalCampaignPageSize,
    };
    getCampaignList(
      queryParams,
      (resStatus, responseString, resJson) => {
        setCampaignsLoading(false);
        if (resStatus == 200 || resStatus == "200") {
          const campaignList = resJson?.data || resJson?.campaigns || [];
          setCampaigns((prev) =>
            append ? [...prev, ...campaignList] : campaignList,
          );
          setCampaignsHasMore(
            campaignList.length === linkModalCampaignPageSize,
          );
          setLinkModalCampaignPage(page);
        } else {
          setCampaigns((prev) => (append ? prev : []));
          setCampaignsHasMore(false);
        }
      },
      userDetails?.jwttoken,
    );
  };

  const handleCampaignScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (Math.ceil(scrollTop + clientHeight) >= scrollHeight - 200) {
      if (!campaignsLoading && campaignsHasMore) {
        fetchCampaignsForLinkModal(linkModalCampaignPage + 1, true);
      }
    }
  };

  useEffect(() => {
    if (
      campaignDropdownOpen &&
      currentView === "link" &&
      campaigns.length === 0
    ) {
      setLinkModalCampaignPage(1);
      setCampaignsHasMore(true);
      fetchCampaignsForLinkModal(1);
    }
  }, [campaignDropdownOpen, currentView]);

  const handleConfirmLinkCampaign = () => {
    showSpinner();

    const payload = {
      couponId: discountToLink?._id || discountToLink?.id,
      campaignId: selectedCampaignForLink || "",
    };

    linkCouponToCampaign(
      payload,
      (resStatus, responseString, resJson) => {
        closeSpinner();
        if (resStatus == 200 || resStatus == "200") {
          setState({ ...state, right: false });
          setDiscountToLink(null);
          setSelectedCampaignForLink(null);
          setCampaignDropdownOpen(false);
          modal(
            resJson?.message ||
              responseString ||
              "Discount linked to campaign successfully!",
            null,
            () => {
              if (page === 1) {
                getDiscounts();
              } else {
                setPage(1);
              }
            },
          );
        } else {
          modal(
            resJson?.message ||
              responseString ||
              "Failed to link discount to campaign.",
          );
        }
      },
      userDetails?.jwttoken,
    );
  };

  const list = (anchor) => (
    <Box
      sx={{ width: 600 }}
      role="presentation"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      {currentView === "link" ? (
        <Box
          sx={getSafariCompatibleBoxSx()}
          role="presentation"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <div className="discount-modal-overlay"></div>
          <div className="right-modal discount-modal">
            <div className="modal-header">
              <div className="modal-title">
                <h2>
                  <strong>Link to Campaign</strong>
                </h2>
                <button
                  className="modal-close-btn"
                  onClick={() => {
                    setState({ ...state, right: false });
                    setDiscountToLink(null);
                    setSelectedCampaignForLink(null);
                    setCampaignDropdownOpen(false);
                  }}
                >
                  <CloseIcon />
                </button>
              </div>
            </div>

            <div className="modal-body">
              <div>
                <div className="form-group">
                  <label>
                    <strong>Assign to campaign</strong>
                  </label>
                  <div
                    className="client-custom-dropdown"
                    ref={campaignDropdownRef}
                  >
                    <button
                      type="button"
                      className={`client-dropdown-trigger ${campaignDropdownOpen ? "open" : ""}`}
                      onClick={() => setCampaignDropdownOpen((prev) => !prev)}
                    >
                      <span className="client-trigger-value">
                        {selectedCampaignForLink
                          ? campaigns.find(
                              (c) =>
                                (c._id || c.id) === selectedCampaignForLink,
                            )?.name || selectedCampaignForLink
                          : "No campaign"}
                      </span>
                      <ChevronDownIcon className="client-trigger-chevron" />
                    </button>
                    {campaignDropdownOpen && (
                      <div
                        className={`client-dropdown-panel campaign-dropdown-panel ${campaignDropdownPosition === "top" ? "dropdown-top" : "dropdown-bottom"}`}
                      >
                        <div
                          className="client-dropdown-options"
                          onScroll={handleCampaignScroll}
                        >
                          <div
                            className="client-option-row campaign-option-row"
                            onClick={() => {
                              setSelectedCampaignForLink("");
                              setCampaignDropdownOpen(false);
                            }}
                          >
                            {!selectedCampaignForLink && (
                              <svg
                                className="campaign-check-icon"
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                              >
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                            <span className="client-option-name">
                              No campaign
                            </span>
                          </div>
                          {campaigns.length === 0 && !campaignsLoading && (
                            <div className="client-dropdown-empty">
                              No campaigns found.
                            </div>
                          )}
                          {campaigns.map((campaign) => (
                            <div
                              key={campaign._id || campaign.id}
                              className="client-option-row campaign-option-row"
                              onClick={() => {
                                setSelectedCampaignForLink(
                                  campaign._id || campaign.id,
                                );
                                setCampaignDropdownOpen(false);
                              }}
                            >
                              {selectedCampaignForLink ===
                                (campaign._id || campaign.id) && (
                                <svg
                                  className="campaign-check-icon"
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2.5"
                                >
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                              )}
                              <span className="client-option-name">
                                {campaign.name}
                              </span>
                            </div>
                          ))}
                          {campaigns.length > 0 && campaignsLoading && (
                            <div className="client-dropdown-loading">
                              Loading more...
                            </div>
                          )}
                        </div>
                        <div
                          className="client-option-row campaign-create-new"
                          onClick={() => {
                            setCampaignDropdownOpen(false);
                            setShowCreateCampaignModal(true);
                          }}
                        >
                          <span>+ Create new campaign</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <p className="coupon-description">
                    Optional internal context for why this code was created.
                  </p>
                </div>
                {discountToLink?.campaigns &&
                  discountToLink?.campaigns?.length > 0 && (
                    <div className="campaign-details-section">
                      <h3>Campaigns</h3>
                      {discountToLink?.campaigns.map((campaign) => (
                        <div
                          key={campaign?._id || campaign?.id}
                          className="campaign-details-card"
                        >
                          <div className="campaign-details-header">
                            <h4 className="campaign-details-title">
                              {campaign?.name}
                            </h4>
                            <span
                              className={`campaign-status-badge ${(campaign?.status || "").toLowerCase()}`}
                            >
                              {campaign?.status}
                            </span>
                          </div>
                          {campaign?.description && (
                            <div className="campaign-details-item">
                              <label>Description</label>
                              <p>{campaign?.description}</p>
                            </div>
                          )}

                          {campaign?.createdAt && (
                            <div className="campaign-details-item">
                              <label>Created At</label>
                              <p>
                                {moment(campaign?.createdAt)
                                  ?.tz("Europe/London")
                                  .format("DD MMM YYYY")}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            </div>

            <div className="modal-footer">
              <div className="d-flex align-items-center g-12">
                <button
                  className="btn btn-primary"
                  onClick={handleConfirmLinkCampaign}
                >
                  Confirm
                </button>
                <button
                  className="btn btn-outline-primary"
                  onClick={() => {
                    setState({ ...state, right: false });
                    setDiscountToLink(null);
                    setSelectedCampaignForLink(null);
                    setCampaignDropdownOpen(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </Box>
      ) : activeTab === "discounts" ? (
        <Box
          sx={getSafariCompatibleBoxSx()}
          role="presentation"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <CreateDiscount
            view={currentView}
            discount={selectedDiscount}
            onClose={toggleDrawer(anchor, false)}
            userDetails={userDetails}
            theCallBackFunc={handleDiscountCreated}
          />
        </Box>
      ) : (
        <Box
          sx={getSafariCompatibleBoxSx()}
          role="presentation"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <CreateCampaign
            view={currentView}
            campaign={selectedDiscount}
            onClose={toggleDrawer(anchor, false)}
            userDetails={userDetails}
            theCallBackFunc={handleCampaignCreated}
          />
        </Box>
      )}
    </Box>
  );

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;

    const inputValue = type === "checkbox" ? checked : value;

    if (name === "search") {
      setSearchValue(inputValue);
    } else if (name === "filter") {
      setSearch("");
      setSearchValue(null);
      setFilter(inputValue);
      setPage(1);
    }
  };

  const getSortClass = (key) => {
    if (sort === `${key}=1`) {
      return "c-list-filter__button c-list-filter__button--icon-up";
    } else if (sort === `${key}=-1`) {
      return "c-list-filter__button c-list-filter__button--icon-down";
    }
    return "c-list-filter__button c-list-filter__button--faded";
  };

  useEffect(() => {
    setCampaignInputValue(campaignPage);
  }, [campaignPage]);

  const getCampaigns = React.useCallback(() => {
    showSpinner();

    const queryParams = {
      page: campaignPage,
      limit: campaignPageSize,
    };

    if (campaignSearch) queryParams.search = campaignSearch;
    if (campaignFilter && campaignFilter !== "all") {
      queryParams.filter = JSON.stringify({ status: campaignFilter });
    }

    getCampaignList(queryParams, getCampaigns_done, userDetails.jwttoken);
  }, [
    campaignPage,
    campaignPageSize,
    campaignSearch,
    campaignFilter,
    userDetails.jwttoken,
  ]);

  const getCampaigns_done = (resStatus, responseString, resJson) => {
    closeSpinner();

    if (resStatus === 401) {
      modal(sessionExpired);
      toScreen(false, navigate, "/logout");
    } else if (resStatus === "400") {
      setCampaigns([]);
      setCampaignTotalPages(1);
      alert(responseString);
    } else if (resStatus === 200 || resStatus === "200") {
      const totalRecords =
        resJson?.totalRecords || resJson?.pagination?.total || 0;
      setCampaignTotalPages(Math.ceil(totalRecords / campaignPageSize) || 1);
      setCampaignTotalDocs(resJson?.totalDocs ?? resJson?.totalRecords ?? 0);

      const fetchedCampaigns =
        resJson?.campaigns ||
        resJson?.payload?.campaigns ||
        resJson?.payload ||
        resJson?.data ||
        [];

      const processedCampaigns = Array.isArray(fetchedCampaigns)
        ? fetchedCampaigns.map((campaign) => ({
            ...campaign,
            discountCodesCount: campaign.discountCodes || 0,
            activeCodesCount: campaign.activeCodes || 0,
            totalUses: campaign.totalUses || 0,
          }))
        : [];

      setCampaigns(processedCampaigns);
    }
  };

  const campaignSearchJob = () => {
    setCampaignSearch(campaignSearchValue);
    setCampaignFilter(campaignSearchValue === "" ? "all" : null);
    setCampaignPage(1);
  };

  const handleCampaignInputChange = (event) => {
    const { name, value, type, checked } = event.target;

    const inputValue = type === "checkbox" ? checked : value;

    if (name === "campaign-search") {
      setCampaignSearchValue(inputValue);
    } else if (name === "campaign-filter") {
      setCampaignSearch("");
      setCampaignSearchValue(null);
      setCampaignFilter(inputValue);
      setCampaignPage(1);
    }
  };

  const handleDeleteSelectedCampaigns = () => {
    if (campaignSelectedCheckbox.length === 0) {
      alert("Please select campaigns to delete");
      return;
    }

    confirm(
      "Delete Campaigns",
      `Are you sure you want to delete ${campaignSelectedCheckbox.length} campaign(s)?`,
      "Cancel",
      "Delete",
      () => {
        showSpinner();
        bulkDeleteCampaigns(
          { campaignIds: campaignSelectedCheckbox },
          (resStatus, responseString, resJson) => {
            closeSpinner();
            if (resStatus == 200 || resStatus == "200") {
              setCampaignSelectedCheckbox([]);
              modal(
                resJson?.message ||
                  responseString ||
                  "Selected campaigns deleted successfully!",
                null,
                () => {
                  // fetchCampaignFilterCount();
                  getCampaigns();
                },
              );
            } else {
              modal(
                resJson?.message ||
                  responseString ||
                  "Failed to delete campaigns.",
              );
            }
          },
          userDetails.jwttoken,
        );
      },
    );
  };

  const getCampaignSortClass = (key) => {
    if (campaignSort === `${key}=1`) {
      return "c-list-filter__button c-list-filter__button--icon-up";
    } else if (campaignSort === `${key}=-1`) {
      return "c-list-filter__button c-list-filter__button--icon-down";
    }
    return "c-list-filter__button c-list-filter__button--faded";
  };

  useEffect(() => {
    if (activeTab === "campaigns") {
      getCampaigns();
    }
  }, [activeTab, getCampaigns]);

  // useEffect(() => {
  //   if (activeTab === "campaigns") {
  //     fetchCampaignFilterCount();
  //   }
  // }, [activeTab, fetchCampaignFilterCount]);

  useEffect(() => {
    getDiscounts();
  }, [getDiscounts]);

  useEffect(() => {
    fetchFilterCount();
  }, [fetchFilterCount]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedCheckbox(
        discounts.map((discount, index) => discount._id || index),
      );
    } else {
      setSelectedCheckbox([]);
    }
  };

  const handleDeleteSelected = () => {
    if (selectedCheckbox.length === 0) {
      alert("Please select discounts to delete");
      return;
    }

    confirm(
      "Delete Discounts",
      `Are you sure you want to delete ${selectedCheckbox.length} discount(s)?`,
      "Cancel",
      "Delete",
      () => {
        showSpinner();
        bulkDeleteDiscounts(
          { couponIds: selectedCheckbox },
          (resStatus, responseString, resJson) => {
            closeSpinner();
            if (resStatus == 200 || resStatus == "200") {
              setSelectedCheckbox([]);
              modal(
                resJson?.message ||
                  responseString ||
                  "Selected discounts deleted successfully!",
                null,
                () => {
                  fetchFilterCount();
                  getDiscounts();
                },
              );
            } else {
              modal(
                resJson?.message ||
                  responseString ||
                  "Failed to delete discounts.",
              );
            }
          },
          userDetails.jwttoken,
        );
      },
    );
  };

  return (
    <div className="l-base-layout__content">
      <div className="d-flex justify-content-between w-100">
        <h1>Admin / Discounts</h1>{" "}
        <div className="my-16 text-end g-4 d-flex align-items-center justify-end">
          {activeTab === "discounts" && selectedCheckbox.length > 0 && (
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleDeleteSelected}
            >
              Delete Selected
            </button>
          )}
          {activeTab === "campaigns" && campaignSelectedCheckbox.length > 0 && (
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleDeleteSelectedCampaigns}
            >
              Delete Selected
            </button>
          )}
          {["right"].map((anchor) => (
            <React.Fragment key={anchor}>
              <Button
                className="btn btn-primary"
                onClick={toggleDrawer(anchor, true, "create")}
              >
                {" "}
                + Create{" "}
                {activeTab === "discounts" ? "Discount" : "Campaign"}{" "}
              </Button>
              <Drawer
                anchor={anchor}
                open={state[anchor]}
                onClose={toggleDrawer(anchor, false)}
                ModalProps={{
                  BackdropProps: {
                    style: { backgroundColor: "rgba(0, 0, 0, 0.2)" },
                  },
                  disableEnforceFocus: true,
                }}
                sx={{
                  boxShadow: "none",
                  "& .MuiDrawer-paper": { boxShadow: "none" },
                }}
              >
                {list(anchor)}
              </Drawer>
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="c-hr"></div>
      <div className="my-16">
        <div className="custom-tabs">
          <div
            className={`tab-item ${activeTab === "discounts" ? "active" : ""}`}
            onClick={() => setActiveTab("discounts")}
          >
            Discount codes
            <span className="badge">{filterCount?.all || 0}</span>
          </div>
          <div
            className={`tab-item ${activeTab === "campaigns" ? "active" : ""}`}
            onClick={() => setActiveTab("campaigns")}
          >
            Campaigns
            <span className="badge">{campaignTotalDocs}</span>{" "}
          </div>
        </div>
      </div>

      {activeTab === "discounts" ? (
        <>
          <div className="c-subheader-info-panel">
            <div className="c-subheader-info-panel__search">
              <form
                className="d-flex align-items-center"
                onSubmit={(e) => {
                  e.preventDefault();
                  searchJob();
                }}
              >
                <div className="search-input-block">
                  <input
                    type="text"
                    value={searchValue || ""}
                    onChange={handleInputChange}
                    name="search"
                    placeholder="Search here..."
                    className="c-subheader-info-panel__input"
                  />
                </div>

                <button
                  type="button"
                  className="c-btn-outline c-btn-outline--white c-btn-outline--small"
                  onClick={() => searchValue !== null && searchJob()}
                >
                  Search
                </button>
              </form>
            </div>
          </div>

          <div className="c-hr"></div>

          <div className="l-row">
            <div className="l-col-100 u-padding-top-0 u-padding-bottom-0">
              <div className="filter-bar">
                <div className="c-checkbox-radio-block">
                  <input
                    type="radio"
                    id="all"
                    name="filter"
                    value="all"
                    onChange={handleInputChange}
                    checked={filter === "all"}
                  />
                  <label htmlFor="all">All ({filterCount?.all})</label>
                </div>
                <div className="c-checkbox-radio-block">
                  <input
                    type="radio"
                    id="active"
                    name="filter"
                    value="active"
                    onChange={handleInputChange}
                    checked={filter === "active"}
                  />
                  <label htmlFor="active">Active ({filterCount?.active})</label>
                </div>
                <div className="c-checkbox-radio-block">
                  <input
                    type="radio"
                    id="scheduled"
                    name="filter"
                    value="scheduled"
                    onChange={handleInputChange}
                    checked={filter === "scheduled"}
                  />
                  <label htmlFor="scheduled">
                    Scheduled ({filterCount?.scheduled})
                  </label>
                </div>
                <div className="c-checkbox-radio-block">
                  <input
                    type="radio"
                    id="expired"
                    name="filter"
                    value="expired"
                    onChange={handleInputChange}
                    checked={filter === "expired"}
                  />
                  <label htmlFor="expired">
                    Expired ({filterCount?.expired})
                  </label>
                </div>
                <div className="c-checkbox-radio-block">
                  <input
                    type="radio"
                    id="revoked"
                    name="filter"
                    value="revoked"
                    onChange={handleInputChange}
                    checked={filter === "revoked"}
                  />
                  <label htmlFor="revoked">
                    Revoked ({filterCount?.revoked})
                  </label>
                </div>
                <div className="c-checkbox-radio-block">
                  <input
                    type="radio"
                    id="used"
                    name="filter"
                    value="used"
                    onChange={handleInputChange}
                    checked={filter === "used"}
                  />
                  <label htmlFor="used">Used ({filterCount?.used})</label>
                </div>
              </div>
            </div>

            <div className="l-col-100">
              <div className="c-hr u-margin-bottom-0"></div>

              <div>
                <div className="c-list-entry c-list-entry__header align-items-center">
                  <div className="c-list-entry__checkbox c-list-filter">
                    <form>
                      <div className="select-all-checkbox">
                        <input
                          type="checkbox"
                          id="select-all"
                          name="select-all"
                          onChange={handleSelectAll}
                          checked={
                            selectedCheckbox.length ===
                              (discounts?.length || 0) &&
                            (discounts?.length || 0) > 0
                          }
                        />
                        <label htmlFor="select-all"></label>
                      </div>
                    </form>
                  </div>
                  <div className="c-list-entry__discountCode c-list-filter">
                    <button
                    // className={getSortClass("codes")}
                    // onClick={(e) => sortDiscounts(e, "codes")}
                    >
                      Discount codes
                    </button>
                  </div>

                  <div className="c-list-entry__jobdate c-list-filter">
                    <button
                    // className={getSortClass("type")}
                    // onClick={(e) => sortDiscounts(e, "type")}
                    >
                      Customer Eligibility
                    </button>
                  </div>
                  <div className="c-list-entry__jobdate c-list-filter">
                    <button
                    // className={getSortClass("type")}
                    // onClick={(e) => sortDiscounts(e, "type")}
                    >
                      Type
                    </button>
                  </div>

                  <div className="c-list-entry__discountStatus c-list-filter">
                    <button
                      className={getSortClass("status")}
                      onClick={(e) => sortDiscounts(e, "status")}
                    >
                      Status
                    </button>
                  </div>
                  <div className="c-list-entry__uses c-list-filter">
                    <button
                      className={getSortClass("uses")}
                      onClick={(e) => sortDiscounts(e, "uses")}
                    >
                      Uses
                    </button>
                  </div>
                  <div className="c-list-entry__startDate c-list-filter">
                    <button
                      className={getSortClass("startDate")}
                      onClick={(e) => sortDiscounts(e, "startDate")}
                    >
                      Start date
                    </button>
                  </div>

                  <div className="c-list-entry__startDate c-list-filter">
                    <button
                      className={getSortClass("endDate")}
                      onClick={(e) => sortDiscounts(e, "endDate")}
                    >
                      End date
                    </button>
                  </div>
                  <div className="c-list-entry__discountAction c-list-filter">
                    Action
                  </div>
                </div>
              </div>

              <div>
                {discounts?.length > 0 ? (
                  discounts?.map(renderDiscounts)
                ) : (
                  <div className="u-text-align-center u-margin-top-bottom-m u-color-red">
                    No Discounts found
                  </div>
                )}
              </div>

              <div className="c-hr u-margin-bottom-0"></div>

              <Pagination
                pageSize={pageSize}
                totalPages={totalPages}
                page={page}
                setPage={setPage}
                inputValue={inputValue}
                setInputValue={setInputValue}
                setPageSize={setPageSize}
              />
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="c-subheader-info-panel">
            <div className="c-subheader-info-panel__search">
              <form
                className="d-flex align-items-center"
                onSubmit={(e) => {
                  e.preventDefault();
                  campaignSearchJob();
                }}
              >
                <div className="search-input-block">
                  <input
                    type="text"
                    value={campaignSearchValue || ""}
                    onChange={handleCampaignInputChange}
                    name="campaign-search"
                    placeholder="Search here..."
                    className="c-subheader-info-panel__input"
                  />
                </div>

                <button
                  type="button"
                  className="c-btn-outline c-btn-outline--white c-btn-outline--small"
                  onClick={() =>
                    campaignSearchValue !== null && campaignSearchJob()
                  }
                >
                  Search
                </button>
              </form>
            </div>
          </div>

          <div className="c-hr"></div>

          <div className="l-row">
            <div className="l-col-100">
              <div className="c-hr u-margin-bottom-0"></div>
              <div>
                <div className="c-list-entry c-list-entry__header align-items-center">
                  <div className="c-list-entry__name c-list-filter">
                    Campaign
                  </div>

                  <div className="c-list-entry__reason c-list-filter">
                    Audience
                  </div>

                  <div className="c-list-entry__jobdate c-list-filter">
                    Discount codes
                  </div>

                  <div className="c-list-entry__jobdate c-list-filter">
                    Active codes
                  </div>

                  <div className="c-list-entry__uses c-list-filter">
                    Total uses
                  </div>
                  <div className="c-list-entry__uses c-list-filter">
                    Status
                  </div>

                  <div className="c-list-entry__startDate c-list-filter">
                    Created
                  </div>

                  <div className="c-list-entry__actions c-list-filter text-center">
                    Action
                  </div>
                </div>
              </div>

              <div>
                {campaigns?.length > 0 ? (
                  campaigns?.map(renderCampaigns)
                ) : (
                  <div className="u-text-align-center u-margin-top-bottom-m u-color-red">
                    No Campaigns found
                  </div>
                )}
              </div>

              <div className="c-hr u-margin-bottom-0"></div>

              <Pagination
                pageSize={campaignPageSize}
                totalPages={campaignTotalPages}
                page={campaignPage}
                setPage={setCampaignPage}
                inputValue={campaignInputValue}
                setInputValue={setCampaignInputValue}
                setPageSize={setCampaignPageSize}
              />
            </div>
          </div>
        </>
      )}

      {showEditModal && editingCampaign && (
        <div className="create-campaign-overlay">
          <CreateCampaign
            view="edit"
            campaign={editingCampaign}
            onClose={handleCloseEditModal}
            userDetails={userDetails}
            theCallBackFunc={handleCampaignCreated}
          />
        </div>
      )}

      <CreateCampaignModal
        show={showCreateCampaignModal}
        onClose={() => setShowCreateCampaignModal(false)}
        onSuccess={(newCampaign) => {
          setCampaigns((prev) => [...prev, newCampaign]);
          setSelectedCampaignForLink(newCampaign._id || newCampaign.id);
          setShowCreateCampaignModal(false);
          getCampaigns();
        }}
        userDetails={userDetails}
      />
    </div>
  );
};

Discounts.propTypes = {
  userDetails: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
};

export default withNavigate(Discounts);
